// =============================================================================
// FILE: packages/renderer/src/components/celebration/CelebrationBanner.tsx
// PURPOSE: Dashboard banner for the "Launch Celebration". Shows a live countdown
//          to the founder's birthday (Oct 20). Unlimited users (RCDD / Master)
//          see an "Enterprise unlocked" message; metered users see remaining AI
//          credits, exports and projects.
// =============================================================================

import { useEffect, useState, type JSX } from 'react';
import { PartyPopper, Sparkles, Cpu, FileDown, FolderKanban } from 'lucide-react';
import { useAuthStore } from '../../stores/auth.store.js';
import { isMasterAdmin } from '@bim/shared/config/masterKey.config.js';
import { useEliteStore } from '../../stores/elite.store.js';
import {
  campaignEndDate, daysUntilCampaignEnd, FREE_LIMITS,
  aiCreditsUsedToday, exportsUsed, projectsUsed,
} from '../../lib/celebration.js';

function Meter({ icon: Icon, label, used, limit }: { icon: typeof Cpu; label: string; used: number; limit: number }): JSX.Element {
  const remaining = Math.max(0, limit - used);
  const pct = Math.min(100, (used / limit) * 100);
  const low = remaining === 0;
  return (
    <div className="flex flex-col gap-1 min-w-[112px]">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.12em]" style={{ color: low ? '#fca5a5' : 'rgba(200,220,225,0.7)' }}>
        <Icon style={{ width: 11, height: 11 }} /> {label}
      </div>
      <div className="text-[12px] font-bold" style={{ color: low ? '#fca5a5' : '#e6fffb' }}>{remaining} / {limit} left</div>
      <div style={{ height: 3, borderRadius: 3, background: 'rgba(255,255,255,0.1)', overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: low ? '#ef4444' : '#5eead4' }} />
      </div>
    </div>
  );
}

export function CelebrationBanner(): JSX.Element {
  const email = useAuthStore((s) => s.user?.email);
  const elite = useEliteStore((s) => s.verified);
  const unlimited = elite || isMasterAdmin(email);
  const [days, setDays] = useState(daysUntilCampaignEnd());
  useEffect(() => { const t = setInterval(() => setDays(daysUntilCampaignEnd()), 60_000); return () => clearInterval(t); }, []);

  const endStr = campaignEndDate().toLocaleDateString(undefined, { month: 'long', day: 'numeric' });

  return (
    <div
      data-testid="celebration-banner"
      className="relative overflow-hidden rounded-2xl px-5 py-4 flex flex-wrap items-center gap-x-6 gap-y-3"
      style={{
        background: unlimited
          ? 'linear-gradient(120deg, rgba(30,24,8,0.9), rgba(10,20,30,0.9))'
          : 'linear-gradient(120deg, rgba(10,30,28,0.9), rgba(10,18,30,0.9))',
        border: `1px solid ${unlimited ? 'rgba(252,211,77,0.4)' : 'rgba(45,212,191,0.35)'}`,
        boxShadow: `0 0 26px ${unlimited ? 'rgba(252,211,77,0.1)' : 'rgba(45,212,191,0.1)'}`,
      }}
    >
      <div className="flex items-center gap-3 flex-1 min-w-[240px]">
        <div style={{ width: 42, height: 42, flexShrink: 0, borderRadius: 12, display: 'grid', placeItems: 'center', background: unlimited ? 'rgba(252,211,77,0.12)' : 'rgba(45,212,191,0.12)', border: `1px solid ${unlimited ? 'rgba(252,211,77,0.4)' : 'rgba(45,212,191,0.4)'}` }}>
          <PartyPopper style={{ width: 20, height: 20, color: unlimited ? '#fcd34d' : '#5eead4' }} />
        </div>
        <div>
          <div className="text-[14px] font-bold" style={{ color: unlimited ? '#fde68a' : '#e6fffb' }}>
            Launch Celebration · Free until {endStr} 🎉
          </div>
          <div className="text-[12px]" style={{ color: 'rgba(200,220,225,0.75)' }}>
            {unlimited
              ? 'Enterprise Edition unlocked — unlimited access. Welcome, Commander.'
              : `Unlock the future of ICT engineering before subscriptions begin · ${days} day${days === 1 ? '' : 's'} left`}
          </div>
        </div>
      </div>

      {unlimited ? (
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ background: 'rgba(252,211,77,0.12)', border: '1px solid rgba(252,211,77,0.4)' }}>
          <Sparkles style={{ width: 13, height: 13, color: '#fcd34d' }} />
          <span className="text-[11px] font-bold uppercase tracking-[0.12em]" style={{ color: '#fde68a' }}>Unlimited</span>
        </div>
      ) : (
        <div className="flex items-center gap-5">
          <Meter icon={Cpu}          label="AI Credits/day" used={aiCreditsUsedToday()} limit={FREE_LIMITS.aiPerDay} />
          <Meter icon={FileDown}     label="Exports"         used={exportsUsed()}        limit={FREE_LIMITS.exportsTotal} />
          <Meter icon={FolderKanban} label="Projects"        used={projectsUsed()}       limit={FREE_LIMITS.projects} />
        </div>
      )}
    </div>
  );
}
